import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrackingTimeline } from "@/components/TrackingTimeline";
import { AiEmailDraft } from "@/components/AiEmailDraft";
import { ClientPortalControls } from "@/components/ClientPortalControls";
import { StatusBadge, HoldBadge, CarrierBadge, fmtDate, fmtDateTime, carrierLabel, downloadStoredFile, openStoredFile, CARRIER_OPTIONS } from "@/components/common";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, UploadSimple, Package, ImageSquare, FileText, DownloadSimple, ClockCounterClockwise, PaperPlaneTilt, Trash, CheckCircle, ArrowsClockwise, AirplaneTilt, QrCode, MagnifyingGlass, PencilSimple, Eye, Images, ArrowSquareOut, Clock } from "@phosphor-icons/react";
import { AwbAmendmentDialog, AwbAmendmentList } from "@/components/AwbAmendments";
import { PatientCardSection } from "@/components/PatientCardSection";
import { ShipmentContentsSection } from "@/components/ShipmentContentsSection";
import { ConfirmationCelebration, hasCelebration } from "@/components/ConfirmationCelebration";

const SHIP_STEPS = [
  { key: "preparing", label: "Preparing shipment" },
  { key: "dispatched", label: "Dispatched — handed to courier/airline" },
  { key: "in_transit", label: "In transit" },
  { key: "arrived", label: "Arrived at destination" },
  { key: "delivered", label: "Delivered" },
];

function PhotoImg({ path, filename }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let cancelled = false;
    const token = localStorage.getItem("token");
    fetch(`${BACKEND_URL}/api/files/${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        if (cancelled) return;
        url = URL.createObjectURL(b);
        setSrc(url);
      })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className="aspect-square bg-muted animate-pulse rounded-sm" />;
  return (
    <div className="relative group">
      <img src={src} alt="Goods" className="aspect-square w-full object-cover rounded-sm border border-border" />
      <button
        onClick={() => downloadStoredFile(path, filename || "goods-photo")}
        className="absolute top-2 right-2 inline-flex items-center gap-1.5 rounded-sm bg-black/60 hover:bg-black/80 text-white px-2.5 py-1.5 text-xs font-medium backdrop-blur-sm transition-opacity opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
        data-testid={`download-photo-${path}`}
      >
        <DownloadSimple size={14} /> Download
      </button>
    </div>
  );
}

export default function ShipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isTeam, user } = useAuth();
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const emptyBatch = () => ({ batch_number: "", expiry_date: "" });
  const [batches, setBatches] = useState([emptyBatch()]);
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [awbBusy, setAwbBusy] = useState(false);
  const [stockBusy, setStockBusy] = useState(false);
  const [deductOpen, setDeductOpen] = useState(false);
  const [stockList, setStockList] = useState([]);
  const [deductQty, setDeductQty] = useState({});
  const [stockSearch, setStockSearch] = useState("");

  const PACK = new Set(["vial","vials","tab","tabs","tablet","tablets","box","boxes","pack","packs","packet","packets","pcs","pc","amp","amps","ampoule","ampoules","ampule","ampules","syringe","syringes","pen","pens","bottle","bottles","capsule","capsules","cap","caps","sachet","sachets","strip","strips","unit","units","piece","pieces","carton","cartons","blister","blisters","injection","inj","tube","tubes","of","x"]);
  const norm = (name) => {
    if (!name) return "";
    const s = String(name).toLowerCase().trim().replace(/(\d)\s*(mg|mcg|ug|ml|g|iu|%)\b/g, "$1 $2").replace(/[^a-z0-9%\s]/g, " ");
    const toks = s.split(/\s+/).filter(Boolean);
    const out = [];
    for (let i = 0; i < toks.length; i++) { const t = toks[i]; if (/^\d+$/.test(t) && PACK.has(toks[i + 1])) { i++; continue; } if (PACK.has(t)) continue; out.push(t); }
    return out.join(" ").trim();
  };

  const openDeduct = async () => {
    setStockSearch("");
    try {
      const { data } = await api.get("/stock");
      const items = data.items || [];
      setStockList(items);
      const already = {};
      (s.stock_deducted_lines || []).forEach((l) => { const k = norm(l.name); already[k] = (already[k] || 0) + Number(l.quantity || 0); });
      const prefill = {};
      (s.products || []).forEach((p) => {
        const key = norm(p.name);
        const row = items.find((i) => i.key === key);
        if (row) {
          const remaining = Math.max(0, (Number(p.quantity) || 0) - (already[key] || 0));
          if (remaining > 0) prefill[row.id] = Math.min(remaining, row.quantity);
        }
      });
      setDeductQty(prefill);
      setDeductOpen(true);
    } catch { toast.error("Could not load stock list"); }
  };

  const confirmDeduct = async () => {
    const lines = stockList.filter((i) => (deductQty[i.id] || 0) > 0).map((i) => ({ name: i.name, quantity: Number(deductQty[i.id]) }));
    if (!lines.length) return toast.error("Choose at least one product and quantity");
    setStockBusy(true);
    try {
      const { data } = await api.post(`/shipments/${id}/deduct-stock`, { lines });
      const sh = data.shortfalls || [];
      if (sh.length) toast.warning(`Deducted. Short on: ${sh.map((x) => `${x.name} (${x.short})`).join(", ")} — raise a PO.`);
      else toast.success("Deducted from stock");
      setDeductOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setStockBusy(false); }
  };
  const fileRef = useRef();
  const awbRef = useRef();

  const load = useCallback(() => {
    api.get(`/shipments/${id}`).then((r) => setS(r.data)).catch(() => toast.error("Shipment not found"));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (s && new URLSearchParams(window.location.search).get("focus") === "awb") setTimeout(() => document.getElementById("awb-section")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300); }, [s?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (status) => {
    try {
      await api.patch(`/shipments/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({ shipment_number: "", carrier: "", tracking_number: "", eta: "", patient_name: "", origin: "", destination: "" });
  const openEdit = () => {
    setEdit({ shipment_number: s.shipment_number || "", carrier: s.carrier || "", tracking_number: s.tracking_number || "", eta: s.eta ? s.eta.slice(0, 10) : "", patient_name: s.patient_name || "", origin: s.origin || "", destination: s.destination || "" });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!edit.shipment_number.trim()) return toast.error("Shipment number is required");
    try {
      await api.patch(`/shipments/${id}`, {
        shipment_number: edit.shipment_number,
        carrier: edit.carrier || null,
        tracking_number: edit.tracking_number || null,
        eta: edit.eta || null,
        origin: edit.origin.trim(),
        destination: edit.destination.trim(),
        ...(s.is_bulk ? { patient_name: edit.patient_name } : {}),
      });
      toast.success("Shipment updated");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [dispMode, setDispMode] = useState("courier");
  const [courierName, setCourierName] = useState("");
  const [airlineName, setAirlineName] = useState("");
  const sendToDispatch = async () => {
    if (dispMode === "courier" && !courierName.trim()) return toast.error("Enter the courier name");
    if (dispMode === "air_cargo" && !airlineName.trim()) return toast.error("Enter the airline name");
    try {
      const { data } = await api.post(`/orders/${s.order_id}/send-to-dispatch`, { mode: dispMode, courier_name: courierName, airline_name: airlineName, shipment_id: s.id });
      toast.success(`Sent to Dispatch (${data.dispatch_number})`);
      setDispatchOpen(false);
      navigate(`/dispatch/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const [docsOpen, setDocsOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [picked, setPicked] = useState({});
  const [docsNote, setDocsNote] = useState("");
  const [docsBusy, setDocsBusy] = useState(false);
  const docCount = (s?.photos?.length || 0) + (s?.awb_documents?.length || 0);
  const openDocs = async () => {
    try {
      const { data } = await api.get(`/shipments/${id}/recipients`);
      setRecipients(data.recipients || []);
      setPicked(Object.fromEntries((data.recipients || []).map((r) => [r.email, true])));
      setDocsNote("");
      setDocsOpen(true);
    } catch { toast.error("Could not load client emails"); }
  };
  const sendDocs = async () => {
    const emails = Object.keys(picked).filter((e) => picked[e]);
    if (!emails.length) return toast.error("Select at least one email");
    setDocsBusy(true);
    try {
      const { data } = await api.post(`/shipments/${id}/email-documents`, { emails, note: docsNote });
      toast.success(`${data.attachments} document(s) sent to ${data.sent_to.length} recipient(s)`);
      setDocsOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setDocsBusy(false); }
  };


  const removePhoto = async (p) => {
    if (!window.confirm("Remove this photo?")) return;
    try { await api.delete(`/shipments/${id}/photos/${p.id}`); toast.success("Photo removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };
  const removeAwb = async (d) => {
    if (!window.confirm("Remove this document?")) return;
    try { await api.delete(`/shipments/${id}/awb-document/${d.id}`); toast.success("Document removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };
  const [rereading, setRereading] = useState(null);
  const rereadAwb = async (d) => {
    setRereading(d.id);
    try {
      const { data } = await api.post(`/shipments/${id}/awb-document/${d.id}/reread`);
      if (data.consignee?.name || data.consignee?.address) toast.success(`Consignee read from AWB: ${data.consignee.name || data.consignee.address}`);
      else toast.warning(data.error || "No consignee details could be read from this document");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not read the document"); }
    finally { setRereading(null); }
  };
  const [scanning, setScanning] = useState(false);
  const [qrInfo, setQrInfo] = useState(null);
  const scanFile = async (f) => {
    setFile(f); setQrInfo(null);
    if (!f) return;
    setScanning(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/shipments/photo-scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.found && data.batches.length) {
        setBatches(data.batches.map((b) => ({ batch_number: b.batch_number || "", expiry_date: b.expiry_date || "" })));
        setQrInfo({ ...data, ok: true });
        toast.success(`QR code read — ${data.batches.length} batch${data.batches.length === 1 ? "" : "es"} filled in`);
      } else if (data.found) {
        setQrInfo({ ...data, ok: false });
      }
    } catch { /* scanning is best-effort */ } finally { setScanning(false); }
  };
  const upload = async () => {
    if (!file) return toast.error("Choose a photo");
    const valid = batches.filter((b) => b.batch_number.trim() || b.expiry_date);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("batches", JSON.stringify(valid));
      fd.append("description", description);
      if (qrInfo?.ok) fd.append("qr_filled", "1");
      await api.post(`/shipments/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      setOpen(false);
      setBatches([emptyBatch()]);
      setDescription("");
      setFile(null);
      setQrInfo(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  const updateBatch = (i, field, val) => setBatches((p) => p.map((b, idx) => (idx === i ? { ...b, [field]: val } : b)));
  const addBatch = () => setBatches((p) => [...p, emptyBatch()]);
  const removeBatch = (i) => setBatches((p) => p.filter((_, idx) => idx !== i));

  const [fxBusy, setFxBusy] = useState(false);
  const trkCarrier = ["fedex", "ups"].includes(s?.carrier) ? s.carrier : null;
  const trkLabel = trkCarrier === "ups" ? "UPS" : "FedEx";
  const trk = trkCarrier ? s?.[trkCarrier] : null;
  const syncFedex = async () => {
    setFxBusy(true);
    try {
      const { data } = await api.post(`/${trkCarrier}/shipments/${id}/sync`);
      toast.success(data.changed ? `${trkLabel}: ${data.status} — status moved to ${data.new_status.replace("_", " ")}` : `${trkLabel}: ${data.status || "no new events"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || `${trkLabel} tracking failed`); }
    finally { setFxBusy(false); }
  };
  const cancelFedex = async () => {
    setFxBusy(true);
    try {
      const { data } = await api.post(`/fedex/shipments/${id}/cancel`);
      toast.success(`FedEx AWB ${data.cancelled_tracking} cancelled — shipment AWB cleared`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "FedEx cancellation failed", { duration: 9000 }); }
    finally { setFxBusy(false); }
  };
  const applyAwbSuggestion = async () => {
    try { await api.post(`/shipments/${id}/apply-awb-suggestion`); toast.success("AWB number updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const dismissAwbSuggestion = async () => { await api.delete(`/shipments/${id}/awb-suggestion`); load(); };
  const uploadAwb = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAwbBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("label", f.name);
      const { data } = await api.post(`/shipments/${id}/awb-document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.awb_applied) toast.success(`AWB document attached — AWB number ${data.extracted_awb} filled in automatically`);
      else if (data.extracted_awb) toast.success(`AWB document attached — found number ${data.extracted_awb}`);
      else if (data.awb_error) toast.warning(`AWB document attached, but the number was not read: ${data.awb_error}`, { duration: 8000 });
      else toast.success("AWB document attached");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setAwbBusy(false);
      if (awbRef.current) awbRef.current.value = "";
    }
  };

  const [confirmBusy, setConfirmBusy] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);
  const [awbReqOpen, setAwbReqOpen] = useState(false);
  const [awbReqNote, setAwbReqNote] = useState("");
  const [awbReqBusy, setAwbReqBusy] = useState(false);
  const requestAwb = async () => {
    setAwbReqBusy(true);
    try { await api.post(`/shipments/${id}/request-awb`, { note: awbReqNote }); toast.success("AWB requested — the Heliopolis team has been notified"); setAwbReqOpen(false); setAwbReqNote(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send request"); }
    finally { setAwbReqBusy(false); }
  };
  const [celebrate, setCelebrate] = useState(false);
  const confirmKind = async (kind) => {
    if (confirmBusy) return;
    setConfirmBusy(true);
    try {
      await api.post(`/shipments/${id}/confirm-${kind}`);
      if (kind === "awb" && hasCelebration(s?.carrier)) setCelebrate(true);
      else toast.success(`${kind === "awb" ? "AWB" : "Photos"} confirmed — thank you`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm", { action: { label: "Retry", onClick: () => confirmKind(kind) } });
    } finally {
      setConfirmBusy(false);
    }
  };
  const confirmAwb = () => confirmKind("awb");

  if (!s) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-8">
      <button onClick={() => navigate("/shipments")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-shipments">
        <ArrowLeft size={16} /> Shipments
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground" data-testid="shipment-source-orders">{s.client_name} · {s.order_numbers?.length > 1 ? s.order_numbers.join(", ") : s.order_number}</p>
            {s.carrier === "brazil_courier" && <span className="rounded-full bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold">Brazil Courier</span>}
            {s.order_ids?.length > 1 && <span className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold" data-testid="shipment-consolidated-badge">Consolidated · {s.order_ids.length}</span>}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-none mt-1">{s.shipment_number}</h1>
          <div className="text-sm text-muted-foreground mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{s.origin || "Origin"} → {s.destination || "Destination"}</span>
            <span>·</span>
            <CarrierBadge carrier={s.carrier} size="md" />
            {s.tracking_number && (
              <>
                <span>·</span>
                {s.tracking_url ? (
                  <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline" data-testid="awb-tracking-link">AWB {s.tracking_number} ↗</a>
                ) : (
                  <span>AWB {s.tracking_number}</span>
                )}
              </>
            )}
            <span>·</span>
            <span>ETA {fmtDate(s.eta)}</span>
            {s.client_reference && (<><span>·</span><span data-testid="shipment-reference">Ref: {s.client_reference}</span></>)}
          </div>
          {s.is_bulk && s.patient_name && (
            <p className="mt-2 text-sm" data-testid="shipment-ptn-name"><span className="text-xs uppercase tracking-wide text-muted-foreground mr-2">Patient (PTN)</span><span className="font-semibold">{s.patient_name}</span></p>
          )}
          {s.patients?.length > 1 && (
            <div className="mt-3" data-testid="shipment-ptn-list">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Patients</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {s.patients.map((p, i) => (
                  <span key={i} className="rounded-sm bg-muted px-2 py-1 text-xs font-medium" data-testid={`shipment-ptn-${i}`}>{p.patient_name || "—"} <span className="text-muted-foreground">· {p.order_number}</span></span>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">{isTeam && <AiEmailDraft shipmentId={s.id} onSent={load} />}<HoldBadge shipment={s} testid="shipment-hold-badge" /><StatusBadge status={s.status} /></div>
      </div>

      {s.products?.length > 0 && !(s.client_no_ptn && !isTeam) && (
        <div className="hp-card overflow-hidden" data-testid="shipment-contents">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="font-heading font-bold flex items-center gap-2"><Package size={18} className="text-primary" /> Shipment contents</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{s.is_bulk ? "Partial bulk shipment — quantities included in this shipment." : "Products and patients included in this shipment."}</p>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
                <th className="px-4 py-2 font-semibold">Product</th>
                <th className="px-4 py-2 font-semibold">Patient</th>
                {s.order_ids?.length > 1 && <th className="px-4 py-2 font-semibold">Order</th>}
                <th className="px-4 py-2 font-semibold text-right">Qty</th>
              </tr>
            </thead>
            <tbody>
              {s.products.map((li, i) => (
                <tr key={i} className="border-t border-border" data-testid={`ship-line-${i}`}>
                  <td className="px-4 py-2 font-medium">{li.name}</td>
                  <td className="px-4 py-2" data-testid={`ship-line-patient-${i}`}>{li.patient_name || "—"}</td>
                  {s.order_ids?.length > 1 && <td className="px-4 py-2 text-muted-foreground">{li.order_number}</td>}
                  <td className="px-4 py-2 text-right">{li.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {isTeam && (
        <div className="flex justify-end gap-2 -mt-4">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setDispatchOpen(true)} data-testid="ship-send-to-dispatch">
            <PaperPlaneTilt size={14} className="mr-1.5" /> Send to Dispatch
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={openEdit} data-testid="edit-shipment-btn">
            Edit shipment details
          </Button>
        </div>
      )}

      <ConfirmationCelebration carrier={s.carrier} reference={s.shipment_number} open={celebrate} onClose={() => setCelebrate(false)} />

      <Dialog open={dispatchOpen} onOpenChange={setDispatchOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send to Dispatch</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Creates a dispatch job linked to order {s.order_number} and shipment {s.shipment_number}. Add the box/AWB documents on the dispatch page.</p>
            <div className="inline-flex rounded-md border border-border bg-muted/40 p-1" data-testid="ship-dispatch-mode-tabs">
              <button type="button" onClick={() => setDispMode("courier")} data-testid="ship-dispatch-mode-courier" className={`px-4 py-2 text-sm font-medium rounded-sm ${dispMode === "courier" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>Courier</button>
              <button type="button" onClick={() => setDispMode("air_cargo")} data-testid="ship-dispatch-mode-air" className={`px-4 py-2 text-sm font-medium rounded-sm ${dispMode === "air_cargo" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>Air Cargo</button>
            </div>
            {dispMode === "courier" ? (
              <div><Label className="text-xs uppercase tracking-wide">Courier name</Label><Input className="mt-1.5 rounded-sm" value={courierName} onChange={(e) => setCourierName(e.target.value)} data-testid="ship-dispatch-courier" /></div>
            ) : (
              <div><Label className="text-xs uppercase tracking-wide">Airline name</Label><Input className="mt-1.5 rounded-sm" value={airlineName} onChange={(e) => setAirlineName(e.target.value)} data-testid="ship-dispatch-airline" /></div>
            )}
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={sendToDispatch} data-testid="ship-confirm-send-to-dispatch">Send to Dispatch</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {isTeam && (
        <div className="hp-card p-6 flex flex-wrap items-center gap-3">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mr-2">Update status</p>
          {SHIP_STEPS.map((st) => (
            <Button key={st.key} variant={s.status === st.key ? "default" : "outline"} size="sm" className="rounded-sm" onClick={() => setStatus(st.key)} data-testid={`set-status-${st.key}`}>
              {st.label}
            </Button>
          ))}
        </div>
      )}

      {s.client_no_ptn ? <ShipmentContentsSection shipment={s} onChanged={load} readOnly={!isTeam} /> : isTeam && <PatientCardSection shipment={s} userName={user?.name} onChanged={load} />}
      {!isTeam && !s.client_no_ptn && s.patient_card?.photos_url && (
        <div className="hp-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="client-photos-card">
          <div className="min-w-0">
            <h3 className="font-heading font-bold flex items-center gap-2"><Images size={18} className="text-primary" /> Shipment photos</h3>
            <p className="text-sm text-muted-foreground mt-1">Photos of your goods taken before dispatch are shared in a Dropbox folder.</p>
          </div>
          <Button asChild className="rounded-sm" data-testid="client-open-dropbox-photos">
            <a href={s.patient_card.photos_url} target="_blank" rel="noopener noreferrer"><ArrowSquareOut size={16} className="mr-2" /> Open Dropbox photos folder</a>
          </Button>
        </div>
      )}

      {isTeam && (
        <div className="hp-card p-6" data-testid="shipment-stock-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-heading font-bold flex items-center gap-2"><Package size={18} className="text-primary" /> Stock</h3>
              {s.stock_deducted
                ? <p className="text-sm text-muted-foreground mt-1" data-testid="stock-deducted-status">Deducted from stock on {fmtDate(s.stock_deducted_at)}{s.stock_deducted_by ? ` by ${s.stock_deducted_by}` : ""}. This can be reversed.</p>
                : <p className="text-sm text-muted-foreground mt-1">Deduct this shipment's items from the stock list when you begin preparing it. This is reversible.</p>}
            </div>
            <div className="flex items-center gap-2">
              <Button className="rounded-sm" disabled={stockBusy} data-testid="deduct-stock-btn" onClick={openDeduct}>
                {s.stock_deducted ? "Deduct more items" : "Deduct from stock"}
              </Button>
              {s.stock_deducted && (
                <Button variant="outline" className="rounded-sm" disabled={stockBusy} data-testid="return-stock-btn"
                  onClick={async () => { setStockBusy(true); try { await api.post(`/shipments/${id}/return-stock`); toast.success("Returned to stock"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setStockBusy(false); } }}>
                  Return all to stock
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog open={deductOpen} onOpenChange={setDeductOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="deduct-stock-dialog">
          <DialogHeader><DialogTitle>Deduct from stock — {s.shipment_number}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Choose which products (and how many) to deduct. Quantities are pre-filled from this shipment where they match a stock item.</p>
          <Input placeholder="Search products…" value={stockSearch} onChange={(e) => setStockSearch(e.target.value)} className="rounded-sm mt-1" data-testid="deduct-stock-search" />
          <div className="border border-border rounded-md overflow-x-auto mt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2 text-right">On hand</th>
                  <th className="px-3 py-2 text-right">Deduct</th>
                </tr>
              </thead>
              <tbody>
                {stockList.filter((i) => !stockSearch || i.name.toLowerCase().includes(stockSearch.toLowerCase())).map((i) => (
                  <tr key={i.id} className="border-b border-border/50 last:border-0" data-testid={`deduct-row-${i.name}`}>
                    <td className="px-3 py-2 font-medium">{i.name}</td>
                    <td className={`px-3 py-2 text-right ${i.quantity <= 0 ? "text-red-600" : ""}`}>{i.quantity}</td>
                    <td className="px-3 py-2 text-right">
                      <Input type="number" min="0" max={i.quantity} value={deductQty[i.id] ?? 0}
                        onChange={(e) => setDeductQty((q) => ({ ...q, [i.id]: Math.max(0, Math.min(Number(e.target.value) || 0, i.quantity)) }))}
                        className="w-20 rounded-sm text-right ml-auto" data-testid={`deduct-qty-${i.name}`} />
                    </td>
                  </tr>
                ))}
                {stockList.length === 0 && <tr><td colSpan={3} className="px-3 py-8 text-center text-muted-foreground">Stock list is empty.</td></tr>}
              </tbody>
            </table>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={confirmDeduct} disabled={stockBusy} data-testid="confirm-deduct-btn">{stockBusy ? "Deducting…" : "Deduct selected"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {isTeam && s.audit && s.audit.length > 0 && (
        <div className="hp-card p-6" data-testid="shipment-audit-trail">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <ClockCounterClockwise size={16} className="text-primary" /> Edit history
          </p>
          <ul className="space-y-2">
            {[...s.audit].reverse().map((a, i) => (
              <li key={i} className="text-sm flex items-center justify-between gap-3">
                <span>{a.action}</span>
                <span className="text-muted-foreground text-xs">{a.by} · {new Date(a.ts).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8 min-w-0 [&>div]:min-w-0">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2"><ImageSquare size={20} className="text-primary" /> Goods Photos</h3>
            {isTeam && (
              <Button className="rounded-sm" onClick={() => setOpen(true)} data-testid="open-upload-photo">
                <UploadSimple size={16} className="mr-2" /> Upload Photo
              </Button>
            )}
          </div>
          {(!s.photos || s.photos.length === 0) ? (
            <div className="border-2 border-dashed border-zinc-300 rounded-md py-16 flex flex-col items-center text-center">
              <Package size={40} className="text-zinc-400" weight="duotone" />
              <p className="font-heading font-bold mt-3">No photos yet</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-xs">Upload photos of the actual goods with batch number and expiry date for each departing shipment.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {s.photos.map((p) => (
                <div key={p.id} className="border border-border rounded-md overflow-hidden bg-card" data-testid={`photo-card-${p.batch_number}`}>
                  <PhotoImg path={p.storage_path} filename={p.original_filename} />
                  <div className="p-3 space-y-2">
                    {p.qr?.auto_batches && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5" data-testid="photo-qr-badge"><QrCode size={11} /> Batch read from QR</span>}
                    {(() => {
                      const rows = (p.batches && p.batches.length ? p.batches : [{ batch_number: p.batch_number, expiry_date: p.expiry_date }]).filter((b) => b.batch_number || b.expiry_date);
                      return rows.map((b, bi) => (
                        <div key={bi} className="flex items-center justify-between border-b border-border/50 last:border-0 pb-1.5 last:pb-0">
                          <div>
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground block">Batch</span>
                            <span className="text-sm font-semibold">{b.batch_number || "—"}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground block">Expiry</span>
                            <span className="text-sm font-semibold">{b.expiry_date ? fmtDate(b.expiry_date) : "—"}</span>
                          </div>
                        </div>
                      ));
                    })()}
                    {p.description && <p className="text-xs text-muted-foreground pt-1">{p.description}</p>}
                    {isTeam && <button onClick={() => removePhoto(p)} className="inline-flex items-center gap-1 text-xs text-destructive hover:underline" data-testid={`photo-remove-${p.id}`}><Trash size={13} /> Remove photo</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {s.photos?.length > 0 && (
            s.photos_confirmed ? (
              <div className="flex items-center gap-2 rounded-sm border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700" data-testid="photos-confirmed-badge">
                <CheckCircle size={16} weight="fill" />
                <span>Photos confirmed by {s.photos_confirmed_by} · {fmtDateTime(s.photos_confirmed_at)}</span>
              </div>
            ) : !isTeam ? (
              <Button className="w-full rounded-sm" onClick={() => confirmKind("photos")} disabled={confirmBusy} data-testid="confirm-photos-btn">
                <CheckCircle size={16} className="mr-2" /> {confirmBusy ? "Confirming…" : "Confirm goods photos"}
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="photos-awaiting-confirmation">Awaiting client confirmation of photos.</p>
            )
          )}
        </div>

        <div className="space-y-8">
          {trkCarrier && s.tracking_number && (
            <div className="hp-card p-6" data-testid="fedex-tracking-card">
              <div className="flex items-start justify-between gap-3 mb-3">
                <h3 className="font-heading font-bold flex items-center gap-2"><AirplaneTilt size={18} className={trkCarrier === "ups" ? "text-[#351C15]" : "text-[#4D148C]"} /> {trkLabel} tracking</h3>
                {isTeam && <Button variant="outline" size="sm" className="rounded-sm h-8" onClick={syncFedex} disabled={fxBusy} data-testid="fedex-sync-btn"><ArrowsClockwise size={14} className={`mr-1.5 ${fxBusy ? "animate-spin" : ""}`} /> {fxBusy ? "Syncing…" : "Sync now"}</Button>}
              </div>
              {trk?.last_sync ? (
                <>
                  <p className="text-sm"><span className="text-xs uppercase tracking-wide text-muted-foreground mr-2">Latest</span><span className="font-semibold" data-testid="fedex-latest-status">{trk.latest_status || "—"}</span></p>
                  {trk.eta && <p className="text-sm mt-1"><span className="text-xs uppercase tracking-wide text-muted-foreground mr-2">{trkLabel} ETA</span>{fmtDate(trk.eta)}</p>}
                  {trk.last_alert && <p className="mt-2 text-xs rounded-sm border border-amber-200 bg-amber-50 text-amber-800 px-2 py-1" data-testid="fedex-alert-badge">Team alerted ({trk.last_alert.code}) on {fmtDateTime(trk.last_alert.ts)}</p>}
                  {trk.last_error && <p className="mt-2 text-xs rounded-sm border border-red-200 bg-red-50 text-red-800 px-2 py-1" data-testid="tracking-last-error">Last sync error: {trk.last_error}</p>}
                  <p className="text-xs text-muted-foreground mt-1">Last checked {fmtDateTime(trk.last_sync)} · refreshes when you open this page and every 2 hours · shipment status follows the scans automatically</p>
                  {trk.scans?.length > 0 && (
                    <details className="mt-3">
                      <summary className="text-sm cursor-pointer text-primary">Scan history ({trk.scans.length})</summary>
                      <ol className="mt-2 space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="fedex-scans">
                        {trk.scans.map((ev, i) => (
                          <li key={i} className="text-xs border-l-2 border-border pl-3">
                            <p className="font-medium">{ev.desc}</p>
                            <p className="text-muted-foreground">{ev.ts ? fmtDateTime(ev.ts) : ""}{ev.location ? ` · ${ev.location}` : ""}</p>
                          </li>
                        ))}
                      </ol>
                    </details>
                  )}
                </>
              ) : <p className="text-sm text-muted-foreground">Not checked yet — {trkLabel} scans are pulled automatically every 2 hours{isTeam ? ", or click Sync now" : ""}.{trk?.last_error ? ` Last error: ${trk.last_error}` : ""}</p>}
              {isTeam && trkCarrier === "fedex" && s.fedex?.created_at && !s.fedex?.cancelled_at && ["preparing", "dispatched"].includes(s.status) && (
                <div className="mt-4 pt-3 border-t border-border">
                  <ConfirmDelete
                    testid="fedex-cancel-awb"
                    label="Cancel FedEx AWB"
                    title={`Cancel FedEx AWB ${s.tracking_number}?`}
                    description="This voids the label with FedEx (only possible before pickup) and clears the AWB number from this shipment. The label document stays in the history marked as cancelled."
                    onConfirm={cancelFedex}
                  />
                </div>
              )}
              {s.fedex?.cancelled_at && <p className="mt-3 text-xs text-muted-foreground" data-testid="fedex-cancelled-note">AWB {s.fedex.cancelled_tracking} was cancelled on {fmtDateTime(s.fedex.cancelled_at)} by {s.fedex.cancelled_by}.</p>}
            </div>
          )}
          {isTeam && (
            <div className="hp-card p-6" data-testid="send-docs-section">
              <h3 className="font-heading font-bold flex items-center gap-2 mb-2"><PaperPlaneTilt size={18} className="text-primary" /> Send Documents by Email</h3>
              <p className="text-sm text-muted-foreground mb-4">Email all {docCount} attached file(s) — goods photos and AWB documents — to the client's saved addresses as secure download links (valid 30 days).</p>
              <Button className="w-full rounded-sm" onClick={openDocs} disabled={docCount === 0} data-testid="open-send-docs">
                <PaperPlaneTilt size={16} className="mr-2" /> Send documents
              </Button>
              {docCount === 0 && <p className="mt-2 text-xs text-muted-foreground">Attach a photo or AWB document first.</p>}
            </div>
          )}
          {/* AWB Documents — team upload beside the goods photos */}
          <div className="hp-card p-6" id="awb-section" data-testid="awb-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold flex items-center gap-2"><FileText size={18} className="text-primary" /> AWB Documents</h3>
              {isTeam && (
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => awbRef.current?.click()} disabled={awbBusy} data-testid="upload-awb-doc">
                  <UploadSimple size={14} className="mr-1" /> {awbBusy ? "Uploading…" : "Attach"}
                </Button>
              )}
              <input ref={awbRef} type="file" className="hidden" onChange={uploadAwb} data-testid="awb-doc-input" />
            </div>
            {isTeam && s.awb_suggestion?.awb && (
              <div className="mb-4 flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm" data-testid="awb-suggestion">
                <span className="flex-1 min-w-0">AWB number read from document: <b className="font-mono">{s.awb_suggestion.awb}</b>{s.tracking_number ? <span className="text-muted-foreground"> (current: {s.tracking_number})</span> : null}</span>
                <Button size="sm" className="rounded-sm h-7" onClick={applyAwbSuggestion} data-testid="apply-awb-suggestion">Use this number</Button>
                <button className="text-muted-foreground hover:text-foreground" onClick={dismissAwbSuggestion} aria-label="Dismiss" data-testid="dismiss-awb-suggestion"><Trash size={14} /></button>
              </div>
            )}
            {(!s.awb_documents || s.awb_documents.length === 0) ? (
              <div className="border-2 border-dashed border-zinc-300 rounded-md py-6 px-3 text-center text-sm text-muted-foreground space-y-3">
                <p>{isTeam ? "Attach the AWB / airway bill or cargo document for this shipment." : "No AWB document attached yet."}</p>
                {s.awb_request?.status && ["open", "booking"].includes(s.awb_request.status) ? (
                  <p className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800" data-testid="awb-requested-badge"><Clock size={13} /> AWB requested {fmtDateTime(s.awb_request.requested_at)}{isTeam ? ` by ${s.awb_request.requested_by}` : " — our team is on it"}</p>
                ) : !isTeam ? (
                  <Button variant="outline" className="rounded-sm" onClick={() => setAwbReqOpen(true)} data-testid="request-awb-btn"><PaperPlaneTilt size={16} className="mr-2" /> Request AWB</Button>
                ) : null}
                {isTeam && s.awb_request?.note && ["open", "booking"].includes(s.awb_request.status) && <p className="text-xs text-amber-900">Client note: “{s.awb_request.note}”</p>}
              </div>
            ) : (
              <div className="space-y-2">
                {s.awb_documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between border border-border rounded-sm px-3 py-2" data-testid={`awb-doc-${d.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={16} className="text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{d.label}</p>
                        <p className="text-xs text-muted-foreground">{fmtDateTime(d.uploaded_at)}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-1 shrink-0">
                      {isTeam && (
                        <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={() => rereadAwb(d)} disabled={rereading === d.id} title="Read consignee from AWB" data-testid={`reread-awb-doc-${d.id}`}>
                          <MagnifyingGlass size={14} className="mr-1" /> {rereading === d.id ? "Reading…" : "Read consignee"}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => openStoredFile(d.storage_path)} title="Open in new tab" data-testid={`open-awb-doc-${d.id}`}>
                        <Eye size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => downloadStoredFile(d.storage_path, d.original_filename)} data-testid={`download-awb-doc-${d.id}`}>
                        <DownloadSimple size={16} />
                      </Button>
                      {isTeam && <Button variant="ghost" size="sm" className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => removeAwb(d)} data-testid={`awb-doc-remove-${d.id}`}><Trash size={16} /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {s.awb_consignee && Object.values(s.awb_consignee).some(Boolean) && (
              <div className="mt-3 rounded-sm border border-border bg-muted/40 px-3 py-2 text-sm" data-testid="awb-consignee">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Consignee (read from AWB)</p>
                <p className="font-medium">{s.awb_consignee.name}{s.awb_consignee.company ? ` · ${s.awb_consignee.company}` : ""}</p>
                <p className="text-muted-foreground">{[s.awb_consignee.address, [s.awb_consignee.city, s.awb_consignee.state].filter(Boolean).join(", "), s.awb_consignee.postal_code, s.awb_consignee.country].filter(Boolean).join(" · ")}</p>
                {s.awb_consignee.phone && <p className="text-muted-foreground">Phone: {s.awb_consignee.phone}</p>}
              </div>
            )}
            {s.awb_documents?.length > 0 && (
              s.awb_confirmed ? (
                <div className="mt-4 flex items-center gap-2 rounded-sm border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700" data-testid="awb-confirmed-badge">
                  <CheckCircle size={16} weight="fill" />
                  <span>AWB confirmed{s.awb_confirmed_by ? ` by ${s.awb_confirmed_by}` : ""}{s.awb_confirmed_at ? ` · ${fmtDateTime(s.awb_confirmed_at)}` : ""}</span>
                </div>
              ) : !isTeam ? (
                <>
                  <Button className="mt-4 w-full rounded-sm" onClick={confirmAwb} disabled={confirmBusy || s.awb_amendment_pending} data-testid="confirm-awb-btn">
                    <CheckCircle size={16} className="mr-2" /> {confirmBusy ? "Confirming…" : s.awb_amendment_pending ? "Awaiting amended AWB" : "Confirm AWB"}
                  </Button>
                  {!s.awb_amendment_pending && (
                    <Button variant="outline" className="mt-2 w-full rounded-sm" onClick={() => setAmendOpen(true)} data-testid="request-awb-amendment-btn">
                      <PencilSimple size={16} className="mr-2" /> Request AWB amendment
                    </Button>
                  )}
                </>
              ) : (
                <p className="mt-4 text-xs text-muted-foreground" data-testid="awb-awaiting-confirmation">{s.awb_amendment_pending ? "Client requested an AWB amendment — replace the AWB below." : "Awaiting client confirmation."}</p>
              )
            )}
            <AwbAmendmentList shipment={s} isTeam={isTeam} onChange={load} />
            {!isTeam && <AwbAmendmentDialog open={amendOpen} onOpenChange={setAmendOpen} endpoint={`/shipments/${id}/awb-amendments`} onDone={load} shipmentNumber={s.shipment_number} />}
            {!isTeam && (
              <Dialog open={awbReqOpen} onOpenChange={setAwbReqOpen}>
                <DialogContent data-testid="request-awb-dialog">
                  <DialogHeader><DialogTitle>Request AWB — {s.shipment_number}</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-foreground">Ask the Heliopolis team to issue or upload the air waybill for this shipment. You'll be notified as soon as it's available.</p>
                  <Textarea className="rounded-sm" placeholder="Optional note (preferred courier, deadline, consignee details…)" value={awbReqNote} onChange={(e) => setAwbReqNote(e.target.value)} data-testid="request-awb-note" />
                  <DialogFooter><Button className="rounded-sm" onClick={requestAwb} disabled={awbReqBusy} data-testid="request-awb-submit">{awbReqBusy ? "Sending…" : "Send request"}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {isTeam && <ClientPortalControls shipment={s} onChange={load} />}

          <div className="hp-card p-6">
            <h3 className="font-heading font-bold mb-4">Tracking</h3>
            <TrackingTimeline shipment={s} />
            <div className="mt-4 space-y-3 border-t border-border pt-4">
              {(s.events || []).slice().reverse().map((e, i) => (
                <div key={i} className="text-sm">
                  <p className="text-foreground capitalize">{String(e.status).replace(/_/g, " ")}{e.note ? ` — ${e.note}` : ""}</p>
                  <p className="text-xs text-muted-foreground">{e.by} · {fmtDateTime(e.ts)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Goods Photo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-zinc-300 rounded-md py-8 flex flex-col items-center cursor-pointer hover:border-primary transition-colors"
              data-testid="photo-dropzone"
            >
              <UploadSimple size={28} className="text-zinc-400" />
              <p className="text-sm mt-2">{file ? file.name : "Click to select an image"}</p>
              {scanning && <p className="text-xs text-primary mt-1" data-testid="qr-scanning">Scanning for QR code…</p>}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => scanFile(e.target.files?.[0] || null)} data-testid="photo-file-input" />
            </div>
            {qrInfo?.ok && (
              <div className="rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="qr-found">
                <p className="font-semibold">QR code read — batch & expiry filled from the photo</p>
                {(qrInfo.meta?.product || qrInfo.meta?.patient) && <p className="mt-0.5">{[qrInfo.meta.product, qrInfo.meta.patient && `PTN ${qrInfo.meta.patient}`, qrInfo.meta.quantity].filter(Boolean).join(" · ")}</p>}
                {qrInfo.batches.some((b) => b.expiry_raw && !b.expiry_date) && <p className="text-amber-700 mt-0.5">Some expiry dates could not be parsed — please check them.</p>}
              </div>
            )}
            {qrInfo && !qrInfo.ok && <p className="text-xs text-muted-foreground" data-testid="qr-nobatch">A QR code was found but no batch/expiry details in it — enter them manually.</p>}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide">Batches & expiry (optional)</Label>
                <Button variant="outline" size="sm" className="rounded-sm h-7" onClick={addBatch} data-testid="add-batch-row">
                  <UploadSimple size={12} className="mr-1 rotate-0" /> Add batch
                </Button>
              </div>
              {batches.map((b, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`batch-row-${i}`}>
                  <Input className="col-span-6 rounded-sm h-9" value={b.batch_number} onChange={(e) => updateBatch(i, "batch_number", e.target.value)} placeholder="B-2026-0451" data-testid={`photo-batch-${i}`} />
                  <Input type="date" className="col-span-5 rounded-sm h-9" value={b.expiry_date} onChange={(e) => updateBatch(i, "expiry_date", e.target.value)} data-testid={`photo-expiry-${i}`} />
                  <button className="col-span-1 flex justify-center text-lg text-muted-foreground hover:text-destructive disabled:opacity-30" onClick={() => removeBatch(i)} disabled={batches.length === 1} data-testid={`remove-batch-${i}`}>
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Description (optional)</Label>
              <Textarea rows={2} className="mt-1.5 rounded-sm" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Pallet 3 of 5, cold-chain sealed…" data-testid="photo-description" />
            </div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={upload} disabled={busy} data-testid="confirm-upload-photo">{busy ? "Uploading…" : "Upload"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={docsOpen} onOpenChange={setDocsOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send shipment documents</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{docCount} file(s) will be included as secure download links: {s.photos?.length || 0} photo(s), {s.awb_documents?.length || 0} AWB document(s).</p>
            <div>
              <Label className="text-xs uppercase tracking-wide">Recipients (client saved emails)</Label>
              {recipients.length === 0 ? (
                <p className="text-sm text-destructive mt-2" data-testid="no-recipients">No emails saved for this client. Add them in Clients → Edit.</p>
              ) : (
                <div className="mt-2 space-y-2">
                  {recipients.map((r) => (
                    <label key={r.email} className="flex items-center gap-3 border border-border rounded-sm px-3 py-2 text-sm cursor-pointer" data-testid={`recipient-row-${r.email}`}>
                      <input type="checkbox" className="h-4 w-4 accent-primary" checked={!!picked[r.email]} onChange={(e) => setPicked({ ...picked, [r.email]: e.target.checked })} data-testid={`recipient-check-${r.email}`} />
                      <span className="font-medium truncate">{r.email}</span>
                      {r.label && <span className="ml-auto text-xs text-muted-foreground">{r.label}</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Note (optional)</Label>
              <Textarea rows={2} className="mt-1.5 rounded-sm" value={docsNote} onChange={(e) => setDocsNote(e.target.value)} placeholder="Please confirm receipt of the attached documents." data-testid="send-docs-note" />
            </div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={sendDocs} disabled={docsBusy || recipients.length === 0} data-testid="confirm-send-docs">{docsBusy ? "Sending…" : "Send email"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Shipment Details</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Shipment number</Label>
              <Input className="mt-1.5 rounded-sm" value={edit.shipment_number} onChange={(e) => setEdit({ ...edit, shipment_number: e.target.value })} data-testid="edit-shipment-number" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Carrier</Label>
              <select className="mt-1.5 w-full h-10 rounded-sm border border-border bg-background px-3 text-sm" value={edit.carrier} onChange={(e) => setEdit({ ...edit, carrier: e.target.value })} data-testid="edit-shipment-carrier">
                <option value="">— Select carrier —</option>
                {CARRIER_OPTIONS.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">AWB / Tracking number</Label>
              <Input className="mt-1.5 rounded-sm" value={edit.tracking_number} onChange={(e) => setEdit({ ...edit, tracking_number: e.target.value })} data-testid="edit-shipment-tracking" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide">Origin</Label>
                <Input className="mt-1.5 rounded-sm" value={edit.origin} onChange={(e) => setEdit({ ...edit, origin: e.target.value })} placeholder="e.g. Dubai, UAE" data-testid="edit-shipment-origin" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide">Destination</Label>
                <Input className="mt-1.5 rounded-sm" value={edit.destination} onChange={(e) => setEdit({ ...edit, destination: e.target.value })} placeholder="e.g. Cairo, Egypt" data-testid="edit-shipment-destination" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">ETA</Label>
              <Input type="date" className="mt-1.5 rounded-sm" value={edit.eta} onChange={(e) => setEdit({ ...edit, eta: e.target.value })} data-testid="edit-shipment-eta" />
            </div>
            {s.is_bulk && (
              <div>
                <Label className="text-xs uppercase tracking-wide">Patient (PTN) name</Label>
                <Input className="mt-1.5 rounded-sm" value={edit.patient_name} onChange={(e) => setEdit({ ...edit, patient_name: e.target.value })} placeholder="Optional" data-testid="edit-shipment-ptn" />
              </div>
            )}
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={saveEdit} data-testid="save-shipment-edit">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
